// Voice mode — T66a (13 Sep 2026), agents doc § 7.4: a conversation with console-vera, hands free.
//
// WHAT IT IS. One button in the kadmon panel opens a loop: she listens, the recruiter stops talking,
// she thinks, she answers in her voice, she listens again — until End, or nothing has been heard
// for a while. Every turn is an ordinary ask in the shared chat: the loop drives the same `ask`
// the composer does, marked spoken, and reads the same answer. Speech stays a transport, and
// nothing here is a second agent (CLAUDE.md § What kadmon is).
//
// NATANEL'S RULES (13 Sep 2026). Her mic is OFF while she speaks — a tap interrupts her, her own
// voice never does, and background noise cannot either. A MUTE keeps the mic closed until it is
// lifted; she finishes what she is saying.
//
// A CARD IS TAPPED (T595, Natanel, 2 Nov 2026: "Remove spoken yes from Talk (Recommended)"). When an
// answer leaves a card waiting she says it waits for their tap, and everything heard after that is
// the next question, a "yes" included: this loop reads no yes or no.
//
// WHY A STORE AND NOT A HOOK. A new chat learns its id from the answer to its first question, and
// the panel remounts on it (app/voice.tsx says the same of the reading). The loop has to survive
// that, so it lives beside the engines in the voice context, and the panel binds fresh callbacks
// each render. No React in this file: voice-mode.test.ts drives it with fakes.
import { withoutDashes } from '@vera/agent/typography';
import type { OrbLevels } from './orb.ts';
import { explainSpeechError, type Listening, type SpeechInput } from './speech.ts';
import { correctionSaid, type LookupInput, type ToolLookup } from './voice-tools.ts';

/** Silence that ends a turn in voice mode — under a second, or the conversation drags. */
export const VOICE_SILENCE_MS = 900;
/** Empty listens in a row before the mode ends on its own: about sixteen seconds of nothing. */
export const VOICE_QUIET_LISTENS = 2;
/** What she says after an answer that left a card waiting. The brain's words, never the model's. */
export const CARD_WAITS_PROMPT = 'It is waiting for your tap on screen.';
export const ENDED_QUIET = 'Nothing was heard for a while, so voice mode ended.';
/** Said when the model reaches for a tool (T66b): the wait on a lookup, heard as speech. */
export const ONE_MOMENT = 'One moment.';
/**
 * What she says, as the stage draws it (T100, 25 Sep 2026): with no dash, like everything else
 * kadmon writes. The words are the voice model’s (T67; ElevenLabs’ since 24 Sep) or a line the
 * brain wrote, and neither passes the guardrail. Cleaned where it is drawn, so a line streamed as
 * far as its dash reads right once the rest arrives.
 */
export const saidLine = (said: string): string => withoutDashes(said);

export type VoiceState = 'off' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'muted';

export interface VoiceSnapshot {
  state: VoiceState;
  /** What is being heard, as it firms up; the last turn's words while she thinks. */
  heard: string;
  /** What she is saying, as she says it (T67, the voice model's transcript); empty on the local loop. */
  said: string;
  muted: boolean;
  /** Why the mode ended, when it ended on its own; null after a deliberate End. */
  note: string | null;
  /** What the last answer left waiting for a tap: the first such card's id, or null. */
  card: string | null;
  /**
   * She has put people, cards or a looked-up person on screen in this conversation (17 Sep 2026):
   * the orb sits at the top so they can be read (voice-stage.ts). Since 24 Sep 2026 it stays set
   * until the conversation ends, rather than clearing at each question — which moved the orb down
   * and up again every time.
   */
  showing: boolean;
}

/** What the panel's ask came to: the answer to read, and a card left waiting for a tap, if any. */
export interface VoiceAnswer {
  answer: string;
  card: string | null;
  /** The answer brought people or cards to the screen. */
  showing: boolean;
  /**
   * 24 Sep 2026: whom the answer as it streamed named, who have no card after all
   * (`FindResponse.unshown`) — the voice has said them, so it says the correction.
   */
  unshown?: string[];
}

/** What the loop hears while an ask runs (T66b): a tool round, and the answer's sentences. */
export interface VoiceProgress {
  working(): void;
  sentence(text: string): void;
  /**
   * The whole answer once the brain has taken it (T202 K-23, 5 Oct 2026: after its matches are
   * written and every check), and how many cards the ask proposed by then: what every voice speaks,
   * the realtime conversation (realtime.ts) and the local loop alike. The sentences are the screen's.
   */
  answer?(text: string, cards: number): void;
}

export interface VoiceCallbacks {
  /**
   * The panel's own ask, marked spoken; null when it failed — the panel shows why. `on` hears
   * the answer as it is written (T66b); an ask that cannot stream calls neither and answers whole.
   */
  ask(text: string, on: VoiceProgress): Promise<VoiceAnswer | null>;
  /**
   * Her voice's own lookups (voice-tools.ts): a plain fact about one named person (23 Sep 2026),
   * who is on a Search and who is waiting on the recruiter (24 Sep), read from the records; the
   * chat's row is written by the brain API and refetched here.
   */
  lookUp(input: LookupInput): Promise<ToolLookup>;
}

/** The reader beside the engines (app/voice.tsx): speaks, says when it is done, can be stopped. */
export interface VoiceReader {
  speak(text: string, done: () => void): void;
  /** An answer read as it is written (T66b): push each sentence, then end. */
  stream(done: () => void): { push(text: string): void; end(): void };
  cancel(): void;
  prime(): void;
}

export interface VoiceModeStore {
  snapshot(): VoiceSnapshot;
  subscribe(listener: () => void): () => void;
  /** The panel's callbacks, rebound on every render so a remounted panel's are the ones used. */
  bind(callbacks: VoiceCallbacks): void;
  start(): void;
  end(): void;
  toggleMute(): void;
  /** While she speaks: stop her and listen. A card she spoke of stays waiting. */
  interrupt(): void;
  /**
   * How loud the recruiter and kadmon are this instant, read by the orb every frame (orb.ts, 16 Sep
   * 2026). Null, or absent, where there is no reading: the orb then moves on the state alone.
   */
  levels?(): OrbLevels | null;
  /**
   * Get ready for a Start that may come (16 Sep 2026): the realtime conversation mints its key
   * and warms its connection while the panel is open, so Talk does not wait on either. Absent
   * where there is nothing to get ready.
   */
  prepare?(): void;
  /**
   * The panel closed, or a conversation began: stop keeping ready (24 Sep 2026 — ElevenLabs keeps
   * its token fresh between `prepare` and this). Absent where nothing is kept.
   */
  unprepare?(): void;
}

export function createVoiceMode(input: SpeechInput, reader: VoiceReader): VoiceModeStore {
  let snap: VoiceSnapshot = {
    state: 'off',
    heard: '',
    said: '',
    muted: false,
    note: null,
    card: null,
    showing: false,
  };
  let callbacks: VoiceCallbacks | null = null;
  let session: Listening | null = null;
  let quiet = 0;
  // Bumped by start and end: a continuation from before the bump — a late answer, a reading that
  // finished after End — finds it changed and does nothing.
  let generation = 0;
  const listeners = new Set<() => void>();

  const set = (patch: Partial<VoiceSnapshot>) => {
    snap = { ...snap, ...patch };
    for (const listener of listeners) listener();
  };

  const end = (note: string | null) => {
    generation += 1;
    session?.abort();
    session = null;
    reader.cancel();
    quiet = 0;
    set({ state: 'off', heard: '', note, card: null, showing: false });
  };

  const listen = () => {
    if (snap.state === 'off') return;
    if (snap.muted) {
      set({ state: 'muted', heard: '' });
      return;
    }
    set({ state: 'listening', heard: '' });
    reader.prime();
    const mine = generation;
    session = input.listen(
      {
        onText: (text) => {
          if (mine === generation) set({ heard: text });
        },
        onEnd: (heard) => {
          session = null;
          if (mine !== generation || snap.state !== 'listening') return;
          if ('text' in heard) {
            quiet = 0;
            void handle(heard.text);
          } else if (heard.error === 'no-speech') {
            quiet += 1;
            if (quiet >= VOICE_QUIET_LISTENS) end(ENDED_QUIET);
            else listen();
          } else if (heard.error !== 'aborted') {
            end(explainSpeechError(heard.error));
          }
        },
      },
      { silenceMs: VOICE_SILENCE_MS, mode: 'talk' },
    );
  };

  const handle = async (text: string) => {
    const mine = generation;
    const bound = callbacks;
    if (!bound) {
      end(null);
      return;
    }
    set({ state: 'thinking', heard: text });
    // The answer once the find has taken it (T202 K-23, his "Wait, then speak"): a tool round is
    // heard as "one moment", and the answer is spoken whole when the stream's answer event comes,
    // after the matches are written and every check. The sentences the stream carries are the
    // screen's, provisional (T66b spoke them as they came, so a draft the find set aside was the
    // one said). An ask that cannot stream pushes nothing, and the whole answer is pushed at the end.
    let streamed = false;
    let sink: ReturnType<VoiceReader['stream']> | null = null;
    const open = () => {
      if (sink) return sink;
      set({ state: 'speaking', heard: '' });
      sink = reader.stream(() => {
        if (mine !== generation || snap.state !== 'speaking') return;
        listen();
      });
      return sink;
    };
    const live = () =>
      mine === generation && (snap.state === 'thinking' || snap.state === 'speaking');
    // What is on screen stays on screen while the next question runs (voice-stage.ts, 24 Sep 2026).
    let answer: VoiceAnswer | null;
    try {
      answer = await bound.ask(text, {
        working: () => {
          if (live() && !streamed) open().push(ONE_MOMENT);
        },
        sentence: () => {},
        answer: (spoken) => {
          if (!live()) return;
          streamed = true;
          open().push(spoken);
        },
      });
    } catch {
      answer = null;
    }
    if (!live()) return;
    if (!answer) {
      // Failed: whatever was said stops, and the panel shows why.
      reader.cancel();
      set({ state: 'listening' });
      listen();
      return;
    }
    set({ card: answer.card, showing: snap.showing || answer.showing });
    const last = open();
    if (!streamed) last.push(answer.answer);
    // 24 Sep 2026: she has read out somebody who has no card after all — she says so.
    if (streamed && answer.unshown && answer.unshown.length > 0) {
      last.push(correctionSaid(answer.unshown));
    }
    if (answer.card) last.push(CARD_WAITS_PROMPT);
    last.end();
  };

  return {
    snapshot: () => snap,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    bind(next) {
      callbacks = next;
    },
    start() {
      if (snap.state !== 'off') return;
      generation += 1;
      quiet = 0;
      set({ state: 'listening', note: null, card: null, showing: false, muted: false });
      listen();
    },
    end: () => end(null),
    toggleMute() {
      const muted = !snap.muted;
      set({ muted });
      if (muted && snap.state === 'listening') {
        session?.abort();
        session = null;
        set({ state: 'muted', heard: '' });
      } else if (!muted && snap.state === 'muted') {
        listen();
      }
    },
    interrupt() {
      if (snap.state !== 'speaking') return;
      reader.cancel();
      listen();
    },
  };
}
